/**
 * usePlanningYears Hook
 * Dynamically calculates planning horizon years based on Setup configuration
 * Replaces hardcoded [2025, 2026, 2027, 2028, 2029] throughout output sections
 * Phase E.1 Implementation
 */

import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Custom hook to get dynamic planning years from business plan setup
 * @param {string} planId - Current business plan ID
 * @returns {Object} { years, startYear, endYear, loading, error, planningSeasonStart, planningSeasonEnd }
 */
export const usePlanningYears = (planId) => {
  const [planningData, setPlanningData] = useState({
    years: [2025, 2026, 2027, 2028, 2029], // Default fallback
    startYear: 2025,
    endYear: 2029,
    planningSeasonStart: null,
    planningSeasonEnd: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const fetchPlanningDates = async () => {
      if (!planId) {
        setPlanningData(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        // Get auth token
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Fetch business plan setup data
        const response = await axios.get(`${BACKEND_URL}/api/business-plans/${planId}`, { headers });
        const plan = response.data;

        // Extract planning season dates
        const seasonStart = plan.planning_season_start;
        const seasonEnd = plan.planning_season_end;

        if (seasonStart && seasonEnd) {
          // Parse dates
          const startDate = new Date(seasonStart);
          const endDate = new Date(seasonEnd);
          
          const startYear = startDate.getFullYear();
          const endYear = endDate.getFullYear();
          
          // Generate array of years
          const years = [];
          for (let year = startYear; year <= endYear; year++) {
            years.push(year);
          }

          setPlanningData({
            years,
            startYear,
            endYear,
            planningSeasonStart: seasonStart,
            planningSeasonEnd: seasonEnd,
            loading: false,
            error: null
          });

          console.log(`✅ Planning horizon: ${startYear}-${endYear} (${years.length} years)`);
        } else {
          // No planning season configured - use defaults
          console.warn('⚠️ No planning season dates found - using default 2025-2029');
          setPlanningData({
            years: [2025, 2026, 2027, 2028, 2029],
            startYear: 2025,
            endYear: 2029,
            planningSeasonStart: '2025-01-01',
            planningSeasonEnd: '2029-12-31',
            loading: false,
            error: null
          });
        }
      } catch (err) {
        console.error('Error fetching planning dates:', err);
        // Fallback to defaults on error
        setPlanningData({
          years: [2025, 2026, 2027, 2028, 2029],
          startYear: 2025,
          endYear: 2029,
          planningSeasonStart: '2025-01-01',
          planningSeasonEnd: '2029-12-31',
          loading: false,
          error: err.message
        });
      }
    };

    fetchPlanningDates();
  }, [planId]);

  return planningData;
};

/**
 * Helper function to format date range display
 * @param {string} startDate - ISO date string
 * @param {string} endDate - ISO date string  
 * @param {string} format - 'short' | 'long' | 'fiscal'
 * @returns {string} Formatted date range
 */
export const formatPlanningDateRange = (startDate, endDate, format = 'short') => {
  if (!startDate || !endDate) return '2025-2029';

  const start = new Date(startDate);
  const end = new Date(endDate);

  switch (format) {
    case 'short':
      return `${start.getFullYear()}-${end.getFullYear()}`;
    
    case 'long':
      const startMonth = start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      return `${startMonth} to ${endMonth}`;
    
    case 'fiscal':
      const fiscalStart = start.toLocaleDateString('en-US', { month: 'short' });
      const fiscalEnd = end.toLocaleDateString('en-US', { month: 'short' });
      return `${fiscalStart} ${start.getFullYear()} - ${fiscalEnd} ${end.getFullYear()}`;
    
    default:
      return `${start.getFullYear()}-${end.getFullYear()}`;
  }
};

/**
 * Helper to check if a specific year is within planning horizon
 * @param {number} year - Year to check
 * @param {Array} planningYears - Array of planning years
 * @returns {boolean}
 */
export const isYearInPlanningHorizon = (year, planningYears) => {
  return planningYears.includes(year);
};

/**
 * Get label for planning period
 * @param {Object} planningData - Object from usePlanningYears
 * @returns {string} Human-readable label
 */
export const getPlanningPeriodLabel = (planningData) => {
  const { years, planningSeasonStart, planningSeasonEnd } = planningData;
  
  if (!planningSeasonStart || !planningSeasonEnd) {
    return `${years.length}-Year Projection`;
  }

  return formatPlanningDateRange(planningSeasonStart, planningSeasonEnd, 'fiscal');
};

export default usePlanningYears;
